import React, { useCallback, useMemo } from "react";

import { addDays, differenceInCalendarDays, isAfter, startOfToday } from "date-fns/esm";
import { useIntl } from "react-intl";

import DateRangeType from "@mapmycustomers/shared/enum/DateRangeType";
import { StandardRange } from "@mapmycustomers/shared/types/range/DateRange";
import { DatePicker } from "@mapmycustomers/ui";

import parseRange from "@app/util/range/parseRange";

interface Props {
  className?: string;
  label?: React.ReactNode;
  onChange?: (value: StandardRange) => void;
  value: StandardRange;
}

const isDateDisabled = (date: Date) => isAfter(date, Date.now());

const DaySelect: React.FC<Props> = ({ className, label, onChange, value }) => {
  const intl = useIntl();

  const pickerValue: Date | undefined = useMemo(() => {
    const parsedValue = parseRange(value);
    if (!parsedValue || parsedValue.dateRangeType !== DateRangeType.DAY) {
      return undefined;
    }
    return addDays(startOfToday(), parsedValue.offset);
  }, [value]);

  const handleChange = useCallback(
    (date: Date | undefined) => {
      if (!date) {
        return;
      }
      onChange?.(`${DateRangeType.DAY}${differenceInCalendarDays(date, Date.now())}`);
    },
    [onChange]
  );

  return (
    <DatePicker
      className={className}
      disabledDate={isDateDisabled}
      format="PPPP"
      label={
        label ??
        intl.formatMessage({
          id: "dateRangeSelect.day",
          defaultMessage: "Select day",
          description: "Day field label in range selector component",
        })
      }
      onChange={handleChange}
      size="middle"
      value={pickerValue}
    />
  );
};

export default DaySelect;
